import { useCallback, useEffect, useRef } from 'react'

import { getMessagingIfSupported, VAPID_KEY } from 'constants/fireabaseConfig'

import { getToken, onMessage, type MessagePayload } from 'firebase/messaging'
import { getAPI } from 'utils/getAPI'

/**
 * FCM Topic Messaging フック
 *
 * - フォアグラウンド通知: onMessage() で受信 (タブがアクティブな状態)
 * - バックグラウンド通知: Service Worker の onBackgroundMessage() で受信
 *
 * ログイン後に subscribeToCompanyTopic を呼び出し、
 * ログアウト時に unsubscribeFromCompanyTopic を呼び出すこと。
 *
 * @param onNotification - 通知受信時に呼び出すコールバック (省略可)
 */
export function useFcmTopic(
  onNotification?: (payload: MessagePayload) => void,
) {
  // onMessage のアンサブスクライブ関数を保持する ref
  const onMessageUnsubscribeRef = useRef<(() => void) | null>(null)
  // コールバックを ref で保持 (useEffect の依存配列に含めずに最新値を参照するため)
  const onNotificationRef = useRef(onNotification)
  useEffect(() => {
    onNotificationRef.current = onNotification
  }, [onNotification])

  // フォアグラウンド通知ハンドラを一度だけ登録する
  useEffect(() => {
    let cancelled = false

    getMessagingIfSupported().then((messaging) => {
      if (!messaging || cancelled) return

      // 既存ハンドラを解除してから再登録
      if (onMessageUnsubscribeRef.current) {
        onMessageUnsubscribeRef.current()
      }

      onMessageUnsubscribeRef.current = onMessage(messaging, (payload) => {
        // console.info('[FCM] フォアグラウンド通知:', payload)
        // カスタムコールバックがあれば呼び出す (UI 通知はアプリ側で制御)
        onNotificationRef.current?.(payload)
      })
    })

    // Local dev フォールバック用: SW から直接送信された push メッセージをハンドリングする
    const handleRawMessage = (event: MessageEvent) => {
      if (event.data && event.data.isLocalDevPush) {
        // console.info('[FCM] フォアグラウンド通知 (ローカルフォールバック):', event.data.payload)
        onNotificationRef.current?.(event.data.payload)
      }
    }
    navigator.serviceWorker?.addEventListener('message', handleRawMessage)

    return () => {
      cancelled = true
      onMessageUnsubscribeRef.current?.()
      onMessageUnsubscribeRef.current = null
      navigator.serviceWorker?.removeEventListener('message', handleRawMessage)
    }
  }, [])

  /**
   * ログイン後: FCM トークンを取得して company トピックにサブスクライブする
   */
  const subscribeToCompanyTopic = useCallback(
    async (companyId: number) => {
      try {
        // console.info(`[FCM] subscribeToCompanyTopic 開始: companyId=${companyId}`)

        const messaging = await getMessagingIfSupported()
        if (!messaging) {
          // console.info('[FCM] このブラウザは Web Push に対応していません')
          return
        }
        // console.info('[FCM] ✓ Messaging インスタンス取得成功')

        // ブラウザから通知許可を取得
        const permission = await Notification.requestPermission()
        // console.info(`[FCM] Notification.permission = ${permission}`)
        if (permission !== 'granted') {
          // console.warn('[FCM] 通知が拒否されました')
          return
        }

        // Service Worker の登録と待機
        // FCM Web Push は SW なしでは動作しない。
        // ローカル開発でも SW を登録して MIME text/html エラーを防ぐ。
        let registration: ServiceWorkerRegistration | undefined
        if ('serviceWorker' in navigator) {
          await navigator.serviceWorker.register('/firebase-messaging-sw.js')
          registration = await navigator.serviceWorker.ready
        }

        // FCM デバイストークンを取得
        // VAPID_KEY が設定されている場合は Web Push 用 VAPID Key を使用する
        const fcmToken = await getToken(messaging, {
          vapidKey: VAPID_KEY,
          serviceWorkerRegistration: registration,
        })
        // console.info(`[FCM] VAPID_KEY ${VAPID_KEY ? '設定済み ✓' : '未設定 (デフォルトキーを使用)'}`)
        if (!fcmToken) {
          // console.warn('[FCM] トークン取得失敗 — VAPID Key が正しいか確認してください')
          return
        }
        // console.info(`[FCM] ✓ トークン取得成功: ${fcmToken.substring(0, 20)}...`)

        // getAPI() 経由でバックエンドのトピックに登録する
        // Authorization ヘッダー・ basePath は getAPI() が一元管理する
        const api = await getAPI()
        if (!api) return

        await api.fcmSubscribeFcmSubscribePost({
          fcmTopicRequest: { fcmToken },
        })
        // console.info('[FCM] /fcm/subscribe レスポンス受信')

        // トークンをセッション中保持（アンサブスクライブ時に使用）
        sessionStorage.setItem('fcm_token', fcmToken)

        // console.info(`[FCM] ✓ トピック登録成功 company_${companyId}`)
      } catch (err) {
        // 通知機能の失敗はアプリ動作に影響させない
        // console.warn('[FCM] サブスクライブエラー:', err)
      }
    },
    [],
  )

  /**
   * ログアウト時: FCM トークンをトピックからアンサブスクライブする
   */
  const unsubscribeFromCompanyTopic = useCallback(async () => {
    const fcmToken = sessionStorage.getItem('fcm_token')

    if (!fcmToken) return

    try {
      const api = await getAPI()
      if (!api) return

      await api.fcmUnsubscribeFcmUnsubscribePost({
        fcmTopicRequest: { fcmToken },
      })
      sessionStorage.removeItem('fcm_token')
      // console.info('[FCM] アンサブスクライブ完了')
    } catch (err) {
      // console.warn('[FCM] アンサブスクライブエラー:', err)
    }
  }, [])

  return { subscribeToCompanyTopic, unsubscribeFromCompanyTopic }
}
